import type { Plan } from '@otter-ai/recall-client';
import {
  CheckoutDialogInteractionEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useTranslations } from 'next-intl';
import { PATH_URL } from '@/constants/path-constants';
import { OtterPlanType, PlanPaymentPeriod } from '@/shared/types/plan';
import { getSavingPercentage } from './utils/price-utils';

type PlanSelectionProps = {
  selectedPlan: Plan;
  planOptions: {
    annualPlan?: Plan;
    monthlyPlan?: Plan;
  };
  onPlanChange: (plan: Plan) => void;
  numSeats: number;
};

export function PlanSelection({
  selectedPlan,
  planOptions,
  onPlanChange,
  numSeats,
}: PlanSelectionProps) {
  const t = useTranslations('settings:billing');
  const { annualPlan, monthlyPlan } = planOptions;

  const annualSavingPercentage = getSavingPercentage(
    Number(annualPlan?.price),
    Number(monthlyPlan?.price),
  );

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2 sm:flex-wrap">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
        <span className="whitespace-nowrap">
          {selectedPlan.type === OtterPlanType.PREMIUM
            ? t('plans.proPlan')
            : t('plans.businessPlan')}
          ,
        </span>
        {monthlyPlan && (
          <div className="flex items-center gap-2 font-semibold">
            <input
              aria-label={monthlyPlan.name}
              type="radio"
              name="billingCycle"
              value={monthlyPlan.id}
              checked={
                selectedPlan.payment_period_type === PlanPaymentPeriod.MONTH
              }
              onChange={(e) => {
                e.stopPropagation();
                onPlanChange(monthlyPlan);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="h-4 w-[16.8px] text-palette-blue-700 accent-palette-blue-700 shrink-0"
            />
            <span>{t('monthly')}</span>
          </div>
        )}
        {annualPlan && (
          <div className="flex items-center font-semibold flex-wrap">
            <input
              aria-label={annualPlan.name}
              type="radio"
              name="billingCycle"
              value={annualPlan.id}
              checked={
                selectedPlan.payment_period_type === PlanPaymentPeriod.YEAR
              }
              onChange={(e) => {
                e.stopPropagation();
                onPlanChange(annualPlan);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="h-4 w-[16.8px] text-palette-blue-700 accent-palette-blue-700 shrink-0 mr-2 "
            />
            <span>{t('annual')}</span>
            {annualSavingPercentage && (
              <span className="ml-1">
                {`(${t('checkout.savePercentage', {
                  percentage: annualSavingPercentage,
                })})`}
              </span>
            )}
            ,
          </div>
        )}
      </div>
      <a
        className="font-semibold underline"
        href={PATH_URL.MANAGE_TEAM}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          trackEvent(
            CheckoutDialogInteractionEvent({
              Action: 'numSeatsClick',
            }),
          );
        }}
      >
        {t('checkout.numSeats', { count: numSeats })}
      </a>
    </div>
  );
}
